import { TFunction } from 'i18next';

import { Contract } from '@/types/enums';

export type ContractConfigNode = {
  id: string;
  labelKey: string;
  children?: ContractConfigNode[];
};

export type ContractOption = {
  value: string;
  label: string;
  children?: ContractOption[];
};

export type TreeContractOption = {
  key: string;
  label: string;
  children?: TreeContractOption[];
};

// Configuration
export const contractsConfiguration: ContractConfigNode[] = [
  {
    id: 'norway',
    labelKey: 'contracts.norway',
    children: [
      {
        id: 'cbn-ost',
        labelKey: 'contracts.regions.cbn_east',
        children: [
          {
            id: `${Contract.ROMERIKE}`,
            labelKey: 'contracts.services.romerike',
          },
          {
            id: `${Contract.AIRPORTCOACH}`,
            labelKey: 'contracts.services.fly_bus',
          },
          // { id: `${Contract.BAERUM}`, labelKey: 'contracts.services.baerum' },
          {
            id: `${Contract.OSLONORDOST}`,
            labelKey: 'contracts.services.oslo_north_east',
          },
          { id: `${Contract.FOLLO}`, labelKey: 'contracts.services.follo' },
          {
            id: `${Contract.OSLOSOR}`,
            labelKey: 'contracts.services.oslo_south',
          },
        ],
      },
      {
        id: 'cbn-vest',
        labelKey: 'contracts.regions.cbn_west',
        children: [
          {
            id: `${Contract.ROGALAND}`,
            labelKey: 'contracts.services.rogaland',
          },
        ],
      },
      {
        id: 'cbn-mid',
        labelKey: 'contracts.regions.cbn_mid',
        children: [
          {
            id: `${Contract.OSTERDALEN}`,
            labelKey: 'contracts.services.osterdalen',
          },
        ],
      },
    ],
  },
  {
    id: 'sweden',
    labelKey: 'contracts.sweden',
    children: [
      {
        id: 'cbs-syd',
        labelKey: 'contracts.regions.cbs_south',
        children: [
          {
            id: `${Contract.BLEKINGE}`,
            labelKey: 'contracts.services.blekinge',
          },
        ],
      },
    ],
  },
];

// Dropdown helper function to build the options for the CascaderSelect component
export const getDropdownContractOptions = (t: TFunction): ContractOption[] =>
  contractsConfiguration.map((country) => ({
    value: country.id,
    label: t(country.labelKey),
    children: country.children?.map((city) => ({
      value: city.id,
      label: t(city.labelKey),
      children: city.children?.map((service) => ({
        value: service.id,
        label: t(service.labelKey),
      })),
    })),
  }));

// Helper that generates antD Tree object (key/label/children) (SoundPerContract)
export const getTreeContractOptions = (
  t: TFunction,
  config: ContractConfigNode[] = contractsConfiguration,
  isTopLevel: boolean = true
): TreeContractOption[] =>
  config.map((node) => ({
    key: node.id,
    label: isTopLevel ? t(node.labelKey + '_plain') : t(node.labelKey),
    children: node.children
      ? getTreeContractOptions(t, node.children, false)
      : undefined,
  }));

// For User Managment List of Contracts

//  helper for filtering config with allowed contracts
const filterConfigByAllowed = (
  nodes: ContractConfigNode[],
  allowed?: number[]
): ContractConfigNode[] => {
  if (!allowed) {
    return nodes;
  }

  if (allowed.length === 0) {
    return [];
  }

  return nodes
    .map((node) => {
      if (node.children?.length) {
        const filteredChildren = filterConfigByAllowed(node.children, allowed);
        return filteredChildren.length
          ? { ...node, children: filteredChildren }
          : null;
      }
      const numId = Number(node.id);
      return !Number.isNaN(numId) && allowed.includes(numId) ? node : null;
    })
    .filter((n): n is ContractConfigNode => n !== null);
};

// Maps any historical region path segment to its current canonical ID.
// Covers the original `norway-*` IDs and the intermediate `cnb-*` IDs
// (typo) that were both superseded by the correct `cbn-*` naming.
export const CONTRACT_PATH_MIGRATIONS: Record<string, string> = {
  'norway-oslo': 'cbn-ost',
  'norway-stavanger': 'cbn-vest',
  'norway-mid': 'cbn-mid',
  'cnb-ost': 'cbn-ost',
  'cnb-vest': 'cbn-vest',
  'cnb-mid': 'cbn-mid',
};

/**
 * Rewrites each segment of a stored contract path using CONTRACT_PATH_MIGRATIONS.
 * Segments with no entry in the map are returned unchanged.
 */
export const migrateContractPath = (path: string[]): string[] =>
  path.map((segment) => CONTRACT_PATH_MIGRATIONS[segment] ?? segment);

export type ContractCountry = 'norway' | 'sweden';

// Country is derived from the contract's placement in contractsConfiguration,
// so new contracts are classified by their tree position without extra wiring.
export const getContractCountry = (
  contractId?: number | null
): ContractCountry | undefined => {
  if (!contractId) {
    return undefined;
  }

  const containsContract = (nodes: ContractConfigNode[]): boolean =>
    nodes.some((node) =>
      node.children?.length
        ? containsContract(node.children)
        : Number(node.id) === contractId
    );

  const country = contractsConfiguration.find(
    (node) => node.children && containsContract(node.children)
  );

  return country ? (country.id as ContractCountry) : undefined;
};

export const isNorwegianContract = (contractId?: number | null) =>
  getContractCountry(contractId) === 'norway';

export const getAllContractIds = (
  allowedContracts?: Contract[]
): Contract[] => {
  if (!allowedContracts) {
    const result: Contract[] = [];

    const collect = (nodes: ContractConfigNode[]) => {
      nodes.forEach((node) => {
        if (node.children?.length) {
          collect(node.children);
        } else {
          const val = Number(node.id);
          if (!Number.isNaN(val)) {
            result.push(val);
          }
        }
      });
    };

    collect(contractsConfiguration);
    return result;
  }

  const filtered = filterConfigByAllowed(
    contractsConfiguration,
    allowedContracts
  );

  const result: Contract[] = [];

  const collect = (nodes: ContractConfigNode[]) => {
    nodes.forEach((node) => {
      if (node.children?.length) {
        collect(node.children);
      } else {
        const val = Number(node.id);
        if (!Number.isNaN(val)) {
          result.push(val);
        }
      }
    });
  };

  collect(filtered);
  return result;
};
